// ============================================================================
// Auth — Google OAuth based session s httpOnly cookie.
// ============================================================================
// Frontend dostane Google ID token, pošle na /api/auth-verify. Backend ho ověří,
// nastaví httpOnly cookie fw40_session=<id_token> (Secure, SameSite=Strict)
// a vrátí session info {email, name, picture, role, linkedTrener, expiresAt}.
// Cookie není přístupná z JavaScriptu — chrání proti XSS.
// Frontend si do localStorage uloží JEN public session info pro UI (kdo je přihlášen),
// nikdy ne ID token.
//
// Sliding window 48 h: každý ověřený API call backend cookie obnoví. User se odhlásí
// jen pokud aplikaci 48 h neotevře (cookie expires) nebo ho ručně odhlásí.

const SESSION_KEY = "fw40_session";
// Legacy klíč — pokud user byl přihlášen před krokem D, localStorage měl ID token.
// Při dalším loginu ho odstraníme; do té doby slouží jako Bearer fallback v apiFetch.
const ID_TOKEN_KEY = "fw40_id_token";
const SESSION_TTL_MS = 48 * 3600 * 1000;
// Owner náhled "zobrazit jako role" — od 12.7.2026 v LOCALSTORAGE, aby ho
// zdědila i nová okna (odkazy "otevřít v novém tabu" z propadlých tréninků,
// komentářů apod. se jinak otevíraly jako owner). Funguje POUZE pro reálného
// ownera; TopBar náhled viditelně ukazuje a jde kdykoli vypnout. Je to náhled
// FRONTENDU (menu + viditelné sekce dané role); backend API stále běží pod reálnou rolí.
const VIEW_AS_KEY = "fw40_view_as";

// Kritické zápisy (session, náhled) nesmí selhat kvůli plné localStorage kvótě
// (api_cache_ ji dřív zaplnil na 5 MB → náhled "zobrazit jako" tiše nešel uložit).
// Když je plno, vyházej api_cache_ a zkus znovu.
function safeLocalSet(key, val) {
  try { localStorage.setItem(key, val); return true; } catch (_) { /* nejspíš QuotaExceeded */ }
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith("api_cache_")) localStorage.removeItem(k);
    }
    localStorage.setItem(key, val);
    return true;
  } catch (_) { return false; }
}

// Přenos owner náhledu do NOVÉ karty (12.7.2026): sessionStorage se do nové
// karty nekopíruje spolehlivě (noopener), takže odkazy otevírané do nového
// okna nesou ?viewas=<role> a tady si ho nová karta převezme (jen pro
// reálného ownera — pro ostatní role se ignoruje).
try {
  const _va = new URLSearchParams(window.location.search).get("viewas");
  if (_va) {
    const _real = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
    // Owner: libovolná role. CSM: jen nutritionist (zástup za výživovou specialistku).
    // Manažer: jen sales (1. 10. 2026, občas navolává a domlouvá zkušební).
    if (_real && (_real.role === "owner" || (_real.role === "csm" && _va === "nutritionist") || (_real.role === "manager" && (_va === "sales" || (_va === "trener" && _real.linkedTrener))))) safeLocalSet(VIEW_AS_KEY, _va);
  }
} catch { /* storage/URL nedostupné */ }

export function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (session.expiresAt && session.expiresAt < Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(ID_TOKEN_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

// Posune expiraci o dalších 48 h. Volej při mountu Layoutu a při každé akci uživatele.
// Tím vznikne sliding window — uživatel zůstává přihlášen pokud chodí aspoň jednou za 48 h.
export function touchSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return;
    const session = JSON.parse(raw);
    // Pokud už platnost vypršela, neprodlužuj (musí se přihlásit znovu)
    if (session.expiresAt && session.expiresAt < Date.now()) return;
    session.expiresAt = Date.now() + SESSION_TTL_MS;
    safeLocalSet(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* ignoruj — localStorage nedostupný */
  }
}

export function getIdToken() {
  return localStorage.getItem(ID_TOKEN_KEY);
}

export function isAuthenticated() {
  return getSession() !== null;
}

// Reálný přihlášený uživatel (bez náhledového přepisu role). Pro switcher a kontrolu práv.
export function getRealUser() {
  return getSession();
}

// Náhledová role. Owner: libovolná role. CSM: jen nutritionist (14.7.2026 —
// Tinka zaskakuje za výživovou specialistku; backend má pro CSM nutri endpointy
// povolené, tohle přepíná jen frontend menu + sekce). null = žádný náhled.
export function getViewAsRole() {
  try {
    const real = getSession();
    if (!real) return null;
    // localStorage (nové: sdílené mezi kartami) s fallbackem na starší sessionStorage.
    const va = localStorage.getItem(VIEW_AS_KEY) || sessionStorage.getItem(VIEW_AS_KEY);
    if (!va || va === real.role) return null;
    if (real.role === "owner") return va;
    if (real.role === "csm" && va === "nutritionist") return va;
    // 1. 10. 2026: manažer přepíná do sales (navolávání, zkušební tréninky).
    if (real.role === "manager" && va === "sales") return va;
    // 30. 10. 2026 (hlášení #186): manažer, který sám trénuje (napojený trenér v Nastavení,
    // dnes Michal Šulc), se přepne do svého trenérského portálu. Vidí jen svoje klientky.
    if (real.role === "manager" && va === "trener" && real.linkedTrener) return va;
    return null;
  } catch {
    return null;
  }
}

// Nastaví/zruší náhled. role = vlastní role / null = zrušit.
// Owner: libovolná role. CSM: jen nutritionist.
export function setViewAsRole(role) {
  try {
    const real = getSession();
    if (!real) return;
    const smiPrepnout = real.role === "owner"
      || (real.role === "csm" && (!role || role === "csm" || role === "nutritionist"))
      || (real.role === "manager" && (!role || role === "manager" || role === "sales" || (role === "trener" && !!real.linkedTrener)));
    if (!smiPrepnout) return;
    if (!role || role === real.role || role === "owner") {
      localStorage.removeItem(VIEW_AS_KEY);
      sessionStorage.removeItem(VIEW_AS_KEY);
    } else {
      safeLocalSet(VIEW_AS_KEY, role);
      sessionStorage.removeItem(VIEW_AS_KEY); // úklid staršího uložení
    }
  } catch {
    /* sessionStorage nedostupný */
  }
}

// Aktuální uživatel pro UI. U reálného ownera s aktivním náhledem vrací přepsanou roli,
// takže routing, sidebar i komponenty se chovají jako daná role.
export function getCurrentUser() {
  const real = getSession();
  if (!real) return null;
  const va = getViewAsRole();
  if (va) return { ...real, role: va, _viewAs: true, _realRole: real.role };
  return real;
}

// Volá se po úspěšném Google login — pošle ID token na backend, dostane session info.
// Backend nastaví httpOnly cookie s ID tokenem (JS ji nevidí). Frontend si ukládá jen
// public session metadata. credentials: "include" zajistí že browser cookie přijme.
export async function loginWithGoogle(idToken) {
  const res = await fetch("/api/auth-verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ idToken }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || "Přihlášení selhalo");
  }
  // Po úspěšném loginu vyčisti legacy ID token z localStorage — od teď je v cookie.
  try { localStorage.removeItem(ID_TOKEN_KEY); } catch {}
  safeLocalSet(SESSION_KEY, JSON.stringify(data));
  return data;
}

// Logout — backend smaže cookie, frontend smaže localStorage + Google auto-select.
// Funguje i když je session expirovaná (auth-logout skipuje auth check).
export async function logout() {
  try {
    await fetch("/api/auth-logout", { method: "POST", credentials: "include" });
  } catch { /* síťová chyba neblokuje lokální logout */ }
  try { localStorage.removeItem(SESSION_KEY); } catch {}
  try { localStorage.removeItem(ID_TOKEN_KEY); } catch {}
  try { localStorage.removeItem(VIEW_AS_KEY); sessionStorage.removeItem(VIEW_AS_KEY); } catch {}
  if (window.google?.accounts?.id) {
    try { window.google.accounts.id.disableAutoSelect(); } catch {}
  }
}

// Helper pro role-checking — true pokud uživatel má jakoukoli z dovolených rolí
export function hasRole(...allowedRoles) {
  const session = getSession();
  if (!session) return false;
  if (allowedRoles.length === 0) return true;
  return allowedRoles.includes(session.role);
}

export const ROLES = {
  OWNER: "owner",
  TRENER: "trener",
  SALES: "sales",
  CSM: "csm",
  MANAGER: "manager",
  // Výživová specialistka (12.7.2026) — analýzy jídelníčků, nutriční konzultace,
  // kontrola masterplánů a jídelníčků na míru. Přístup: jen sekce Klienti;
  // v detailu hlavička (info + nárok na výživu) a editovatelná sekce Výživa.
  NUTRITIONIST: "nutritionist",
};


/**
 * Kam patří člověk po přihlášení, podle role. Bylo to schované v App.jsx,
 * ale potřebuje to i horní lišta (nová záložka), tak to sedí u rolí.
 */
export function defaultPathForRole(role) {
  if (role === "trener") return "/trener/dashboard";
  if (role === "manager") return "/manager/dashboard";
  if (role === "csm") return "/csm/dashboard";
  if (role === "nutritionist") return "/nutri/dashboard";
  // 30. 9. 2026: sales začíná rovnou frontou k volání.
  if (role === "sales") return "/sales/dnes";
  return "/dashboard";
}
